// /teams — list teams the current user is a member of. Empty-state nudges
// toward creating the first team.

import { useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useMyTeams } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import { createTeam } from '../../lib/mutations.js'

export function Teams() {
  const [user] = useAuthState(auth)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: teams, loading, error } = useMyTeams(user?.uid)
  // TeamSwitcher's "Create new team" deep-links here with ?create=1 so the
  // inline form opens without an extra click. See #65.
  const [creating, setCreating] = useState(searchParams.get('create') === '1')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  async function handleCreate(e) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    setFormError(null)
    try {
      const teamId = await createTeam({
        name: name.trim(),
        ownerUid: user.uid,
        ownerEmail: user.email,
        ownerDisplayName: user.displayName,
      })
      navigate(`/teams/${teamId}`)
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Teams"
        subhead={`Signed in as ${user?.email ?? '(unknown)'}.`}
        actions={
          !creating && (
            <Button onClick={() => setCreating(true)} variant="primary" size="sm">
              Create team
            </Button>
          )
        }
      />

      <div className="mx-auto max-w-4xl px-6 py-8">
        {creating && (
          <form
            onSubmit={handleCreate}
            className="mb-8 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-5"
          >
            <h2 className="mb-3 text-sm font-medium text-[var(--color-basalt)]">New team</h2>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                Team name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Acme Engineering"
                className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
              />
            </label>
            {formError && (
              <p className="mt-2 text-xs text-[var(--color-failed)]" role="alert">
                {formError}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <Button
                type="submit"
                disabled={submitting || !name.trim()}
                variant="primary"
                size="sm"
              >
                {submitting ? 'Creating…' : 'Create team'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreating(false)
                  setName('')
                  setFormError(null)
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading && <p className="text-sm text-[var(--color-schist)]">Loading teams…</p>}
        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}
        {!loading && teams.length === 0 && !creating && (
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8 text-center">
            <p className="mb-4 text-sm text-[var(--color-slate)]">
              You're not on any teams yet. Create one to get started.
            </p>
            <Button onClick={() => setCreating(true)} variant="primary" size="sm">
              Create your first team
            </Button>
          </div>
        )}

        {teams.length > 0 && (
          <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            {teams.map((t) => (
              <li key={t.teamId}>
                <Link
                  to={`/teams/${t.teamId}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-[var(--color-sandstone)]/40"
                >
                  <div>
                    <div className="font-medium text-[var(--color-basalt)]">{t.teamId}</div>
                    <div className="text-xs text-[var(--color-schist)]">role: {t.role}</div>
                  </div>
                  <span className="text-[var(--color-schist)]" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
